/**
 * 63.19 step 2 (DECISIONS 332; ARCHITECTURE §5.38; with GameConfig.vehicleImpact): a bot on foot steps out of a moving
 * hull's path. A hull rolling at half a walk or more (the rule's least closing speed), on the bot's planet, with the bot
 * ahead of it within its path's width and its nose due within `HULL_STEP.dueSeconds`, sends the bot sideways across the
 * hull's heading, away from its line (the side it is on; the other when that side is blocked, as the fire lanes' step
 * does), its aim and fire kept. Friend or enemy hull alike: a friend's shoves, an enemy's strikes.
 *
 * Step 3 (the owner: "What really matters is my ability to do vehicle damage to enemy"): only a hull it has noticed, and
 * only after its reaction (`hullGate`). It notices a due hull it sees — within its perception cone round its facing — or
 * one it hears close (its nose within `hearPeople`), and steps its reaction time later (the skill's, as at a new target,
 * jittered 0.75–1.25 by a hash of the bot, the hull and the tick it noticed; `busySeconds` more with a target in sight). A
 * bot looking the other way, or busy in a fight, is struck; an alert one facing the hull steps clear. Kept on the brain
 * (`hullNotice`) while the hull stays due.
 *
 * Read from the start-of-tick state; the moving hulls listed once a tick (`movingHulls`, cached by the caller).
 */
import type { GameState, PlayerState, UnitIntent, Vec3 } from '../types';
import { PERCEPTION_CONE_HALF_ANGLE, PERSON_BODY_HEIGHT, PHYS_DT, VEHICLE_IMPACT } from '../constants';
import { vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { drives } from '../core/body';
import { driveSpec } from '../core/drive';
import { reachBound } from '../core/vehicleImpact';
import { dcos } from '../core/dmath';
import { personSetOf } from '../core/scaleProfile';
import { headingOf, projectToTangent, rightOf } from '../core/surfaceWalk';
import { sideClear } from './fireLanes';

export const HULL_STEP = {
  /** Seconds before the hull's nose reaches it that a bot steps aside. */
  dueSeconds: 2,
  /** People of clearance past the hull's half-width and the bot's radius. */
  marginPeople: 0.5,
  /** Step 3: a due hull's nose this near (people) is heard, whichever way the bot faces. */
  hearPeople: 2,
  /** Step 3: seconds more before a bot with a target in sight steps (its eyes on the fight). */
  busySeconds: 0.5,
};

export interface MovingHull { id: number; planetIndex: number; at: Vec3; heading: Vec3; speed: number; reach: number; half: number }

/** The hulls rolling at the rule's least closing speed or more: where, which way (signed by gear), how fast, how far they reach (at most: no shape built). */
export function movingHulls(state: GameState): MovingHull[] {
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, least = VEHICLE_IMPACT.minWalks * ps.walkSpeed;
  const out: MovingHull[] = [];
  for (const s of state.structures) {
    if (!drives(s) || s.health <= 0 || !s.drive || Math.abs(s.drive.speed) < least) continue;
    const at = vecNormalize(s.surfaceDir), h = projectToTangent(s.drive.heading, at);
    if (vecLength(h) < 1e-12) continue;
    out.push({ id: s.id, planetIndex: s.planetIndex, at, heading: vecScale(vecNormalize(h), s.drive.speed >= 0 ? 1 : -1), speed: Math.abs(s.drive.speed), reach: reachBound(s.type) * person, half: driveSpec(s.type).radius * person });
  }
  return out;
}

/** A hull due to run a unit over: which side of its line the unit is on (±1 by the hull's right), how far its nose is (world units), and the way to it from the unit (across the ground). */
export interface HullDue { hull: MovingHull; side: number; gap: number; toHull: Vec3 }

/** The hull due to run the unit over, if any: the one whose nose arrives soonest. */
export function hullDue(state: GameState, unit: PlayerState, hulls: readonly MovingHull[]): HullDue | null {
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, R = state.planets[unit.planetIndex].radius;
  const pd = vecNormalize(unit.surfaceDir);
  let best: HullDue | null = null, soonest = Infinity;
  for (const h of hulls) {
    if (h.planetIndex !== unit.planetIndex) continue;
    const d = vecSub(pd, h.at), rel = vecScale(vecSub(d, vecScale(h.at, vecDot(d, h.at))), R);   // (across the ground, at its length: projectToTangent normalises)
    const along = vecDot(rel, h.heading);
    if (along <= 0 || (along - h.reach) / h.speed > HULL_STEP.dueSeconds) continue;
    const across = vecDot(rel, rightOf(h.heading, h.at));
    if (Math.abs(across) > h.half + ps.unitRadius + HULL_STEP.marginPeople * person) continue;
    const t = (along - h.reach) / h.speed;
    if (t < soonest) { soonest = t; best = { hull: h, side: across >= 0 ? 1 : -1, gap: along - h.reach, toHull: vecScale(rel, -1) }; }
  }
  return best;
}

/** Is the unit on foot, where a hull can run it over? */
const onFoot = (unit: PlayerState): boolean => !unit.mount && !unit.boarded && !unit.airborne && !unit.rampWalk && unit.piloting === undefined;

/** The bot's intent, stepped out of a due hull's path (unchanged when none is due, the unit is not on foot, or both sides are blocked); `due` when the caller has it. */
export function stepFromHulls(state: GameState, unit: PlayerState, intent: UnitIntent, hulls: readonly MovingHull[], due: HullDue | null = hulls.length && onFoot(unit) ? hullDue(state, unit, hulls) : null): UnitIntent {
  if (!due || !onFoot(unit)) return intent;
  const ps = personSetOf(state), n = vecNormalize(unit.surfaceDir);
  const across = vecNormalize(projectToTangent(rightOf(due.hull.heading, due.hull.at), n));
  const sgn = [due.side, -due.side].find(g => sideClear(state, unit, vecScale(across, g), ps));
  if (sgn === undefined) return intent;
  // across the hull's heading, in the unit's own frame
  const dir = vecScale(across, sgn), heading = headingOf(unit);
  const { cover: _c, dig: _d, ...rest } = intent;
  return { ...rest, forward: vecDot(dir, heading), right: vecDot(dir, rightOf(heading, n)) };
}

/** Step 3: the hull a bot has noticed coming (its id) and the tick it noticed it. */
export interface HullNotice { hull: number; tick: number }

/** Does the unit notice the due hull: in its perception cone round its facing, or its nose heard within `hearPeople`? */
export function hullNoticed(state: GameState, unit: PlayerState, due: HullDue): boolean {
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  if (due.gap <= HULL_STEP.hearPeople * person) return true;
  const n = vecNormalize(unit.surfaceDir), to = projectToTangent(due.toHull, n);
  return vecDot(headingOf(unit), to) >= dcos(PERCEPTION_CONE_HALF_ANGLE);
}

/** 0–1 from the bot, the hull and the tick it noticed it (the reaction's jitter, without the bot's draws). */
function noticeJitter(unit: number, hull: number, tick: number): number {
  let h = Math.imul(unit + 1, 0x9e3779b1) ^ Math.imul(hull + 1, 0x85ebca77) ^ Math.imul(tick + 1, 0xc2b2ae3d);
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d);
  h = Math.imul(h ^ (h >>> 15), 0x846ca68b);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/**
 * Step 3: whether the bot steps from a due hull this tick — noticed (`hullNoticed`; once noticed it stays so while that
 * hull is due), and its reaction (seconds: the skill's; `busy`, a target in sight, adds `busySeconds`) past since — and
 * the notice to keep (none when no hull is due, or it has not noticed the one that is).
 */
export function hullGate(state: GameState, unit: PlayerState, hulls: readonly MovingHull[], tick: number, reaction: number, busy: boolean, prev: HullNotice | undefined): { due: HullDue | null; notice: HullNotice | undefined; go: boolean } {
  const due = hulls.length && onFoot(unit) ? hullDue(state, unit, hulls) : null;
  if (!due) return { due, notice: undefined, go: false };
  const notice = prev?.hull === due.hull.id ? prev : hullNoticed(state, unit, due) ? { hull: due.hull.id, tick } : undefined;
  if (!notice) return { due, notice, go: false };
  const delay = (reaction + (busy ? HULL_STEP.busySeconds : 0)) * (0.75 + 0.5 * noticeJitter(unit.id, notice.hull, notice.tick));
  return { due, notice, go: (tick - notice.tick) * PHYS_DT >= delay };
}
